/* =====================================================================
   Cave Larégnère — SOMMELIER NUMÉRIQUE · INTERFACE
   =====================================================================
   UN SEUL ÉCRAN. Deux champs : ce qu'on sert, et combien on est.
   Les bouteilles s'affichent en dessous, et on choisit en cliquant.

   POURQUOI CETTE FORME
   La première version demandait de remplir six écrans avant de voir la
   moindre bouteille. C'était un questionnaire, pas un conseil : on
   abandonnait avant la fin. Ici, la récompense arrive tout de suite, et
   tout le reste — durée, budget, préférences, sans alcool — est
   facultatif, replié, et ne bloque jamais le passage.

   LE CHOIX EST LIBRE
   Chaque moment du repas propose quelques bouteilles étagées en prix.
   Le client prend celle qu'il veut, où il veut : un petit prix sur
   l'apéritif et une belle bouteille sur le plat, c'est le comportement
   normal, pas une option à activer. Le total se met à jour à chaque
   clic, et la barre du bas reste sous les yeux.

   FABRICATION
   • Aucune dépendance, aucun framework, chargement différé.
   • Tout texte venant du catalogue ou du client passe par `esc()`.
   • L'ajout au panier réutilise le panier Eurus (POST /cart/add.js +
     Section Rendering API) : pas de panier parallèle.
   • Le clavier fait tout ce que fait la souris.
   ===================================================================== */
(function (w, d) {
  'use strict';

  var Moteur = w.CLSommelierMoteur;
  var Savoir = w.CLSommelierSavoir;
  if (!Moteur || !Savoir) return;

  var racine = d.querySelector('[data-cl-sommelier]');
  if (!racine) return;

  var VERSION = '7.0.0';

  var CFG = lireConfig();
  appliquerReglages();

  /* Trois fichiers, trois empreintes de cache : si le navigateur en a
     gardé un ancien, le savoir et le moteur ne se comprennent plus et
     l'outil ne trouve plus rien — sans erreur visible. On le dit
     franchement plutôt que de laisser chercher. */
  var versionsOk = (Savoir.version === VERSION) && (Moteur.version === VERSION);

  /* Mode diagnostic : /pages/…?sommelier=debug
     Affiche l'état réel des données sous les résultats. Sert à comprendre
     une page qui « ne trouve rien » sans avoir à décrire un écran. */
  var DEBUG = false;
  try { DEBUG = new URLSearchParams(w.location.search).get('sommelier') === 'debug'; } catch (e) {}

  var zoneSaisie = racine.querySelector('[data-saisie-zone]');
  var zoneResultat = racine.querySelector('[data-resultats]');
  var barre = racine.querySelector('[data-barre]');

  /* =====================================================================
     ÉTAT
     ===================================================================== */

  var etat = {
    menu: '',
    convives: 6,
    /* Tout ce qui suit est facultatif : l'outil fonctionne entièrement
       sans qu'on y touche. */
    sansAlcool: 0,
    niveau: 'moderee',
    typeRepas: 'diner',
    budgetMax: 0,
    preferences: [],
    /* Couleurs et rayons demandés pour TOUT le repas. Vides = le caviste
       décide, ce qui reste le cas le plus fréquent. */
    couleurs: [],
    categories: [],
    /* Cocktail d'ouverture : tiré au sort à chaque nouvelle demande, et
       stable tant qu'on retouche la même sélection. */
    cocktail: null,
    cocktailsVus: [],
    /* Choix du client, ligne par ligne. */
    couleurLigne: {}, /* cle -> couleur imposée à ce plat seul, ou '' */
    plus: {},         /* cle -> true si le client a déplié les autres bouteilles */
    aTouche: false, /* le client a-t-il retouché la sélection ? */
    choix: {},      /* cle -> handle du produit retenu, ou null si écarté */
    qte: {},        /* cle -> quantité forcée à la main */
    magnum: {}      /* cle -> true si le magnum est retenu */
  };

  var donnees = { catalogue: null, recettes: null, charge: false, erreur: null };
  var resultat = null;   /* { lignes: [...] } */

  /* =====================================================================
     OUTILS
     ===================================================================== */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function prix(n) {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: (CFG.boutique && CFG.boutique.devise) || 'EUR',
        minimumFractionDigits: n % 1 === 0 ? 0 : 2
      }).format(n);
    } catch (e) { return (Math.round(n * 100) / 100) + ' €'; }
  }
  function lireConfig() {
    var el = racine.querySelector('[data-cl-sommelier-config]');
    try { return JSON.parse(el.textContent); }
    catch (e) { return { textes: {}, options: {}, liens: {}, boutique: {}, quantites: {}, gammes: [] }; }
  }
  function opt(nom, defaut) {
    var v = CFG.options ? CFG.options[nom] : undefined;
    return v === undefined ? defaut : v;
  }
  function listeDe(txt) {
    return String(txt || '').split(/[\n,]+/).map(function (x) { return x.trim(); }).filter(Boolean);
  }
  /* Les métachamps sont saisis à la main : « 16 à 18°C », « Entre 16 et
     18 », « 18 », et parfois un simple tiret quand il n'y a rien à dire.
     On en tire une écriture unique, et on n'affiche rien plutôt qu'un
     tiret orphelin — « Ouverture : - » n'informe personne. */
  function texteUtile(v) {
    var t = String(v == null ? '' : v).trim();
    if (!t || t === '-' || t === '—' || t === '/' || t === 'N/A' || t === '.') return '';
    return t;
  }
  function formaterTemperature(v) {
    var t = texteUtile(v);
    if (!t) return '';
    var nombres = t.match(/\d+([.,]\d+)?/g);
    if (!nombres || !nombres.length) return t;
    if (nombres.length >= 2) return nombres[0] + '–' + nombres[1] + ' °C';
    return nombres[0] + ' °C';
  }

  function nomEtape(id) {
    var e = Savoir.etapes.filter(function (x) { return x.id === id; })[0];
    return e ? e.nom : '';
  }

  function nomCategorie(ids) {
    var liste = (ids || []).map(function (id) {
      var c = (Savoir.categories || []).filter(function (x) { return x.id === id; })[0];
      return c ? c.nom.toLowerCase() : id;
    });
    if (!liste.length) return '';
    if (liste.length === 1) return liste[0];
    return liste.slice(0, -1).join(', ') + ' ou ' + liste[liste.length - 1];
  }

  /* Ajoute ou retire une valeur d'une liste, sur place. */
  function basculer(liste, valeur) {
    var i = liste.indexOf(valeur);
    if (i > -1) liste.splice(i, 1); else liste.push(valeur);
    return liste;
  }

  /* Le nom lisible d'une ou plusieurs couleurs, tel qu'on le dit à voix
     haute : « du rouge », « du rouge ou du rosé ». */
  function nomCouleur(ids) {
    var liste = (ids || []).map(function (id) {
      var c = (Savoir.couleurs || []).filter(function (x) { return x.id === id; })[0];
      return c ? c.nom.toLowerCase() : id;
    });
    if (!liste.length) return '';
    if (liste.length === 1) return liste[0];
    return liste.slice(0, -1).join(', ') + ' ou ' + liste[liste.length - 1];
  }

  /* Recopie dans le savoir ce qui a été réglé dans l'éditeur Shopify. */
  function appliquerReglages() {
    var q = CFG.quantites || {};
    Savoir.niveaux_conso.forEach(function (n) { if (q[n.id]) n.cl = q[n.id]; });
    if (q.sans_alcool) Savoir.cl_sans_alcool_par_personne = q.sans_alcool;
    if (q.marge != null) Savoir.marge_securite = q.marge / 100;
    if (q.seuil_magnum) Savoir.seuil_magnum = q.seuil_magnum;
    if (CFG.textes && CFG.textes.aucun_produit) Savoir.textes.aucun_produit = CFG.textes.aucun_produit;
    if (CFG.textes && CFG.textes.label_menu) Savoir.textes.label_menu = CFG.textes.label_menu;
    if (CFG.textes && CFG.textes.label_conso) Savoir.textes.label_conso = CFG.textes.label_conso;

    /* Étiquettes de budget : un champ vidé dans l'éditeur retombe sur le
       mot par défaut plutôt que de laisser une carte muette au milieu
       d'une rangée étiquetée. */
    if (CFG.etiquettes_budget && CFG.etiquettes_budget.length) {
      Savoir.etiquettes_budget = (Savoir.etiquettes_budget || CFG.etiquettes_budget).map(function (defaut, i) {
        var v = CFG.etiquettes_budget[i];
        return (v && String(v).trim()) ? String(v).trim() : defaut;
      });
    }

    CFG.regles = null;
    if (CFG.regles_json) {
      try {
        var r = JSON.parse(CFG.regles_json);
        if (r && typeof r === 'object') CFG.regles = r;
      } catch (e) {
        if (w.console) w.console.warn('[Sommelier] Règles additionnelles ignorées : JSON invalide.');
      }
    }
  }

  function contexte(etapes) {
    return {
      nbAdultes: etat.convives,
      nbSansAlcool: etat.sansAlcool,
      typeRepas: etat.typeRepas,
      duree: (Savoir.types_repas.filter(function (t) { return t.id === etat.typeRepas; })[0] || {}).duree || 150,
      niveau: etat.niveau,
      etapes: etapes,
      mode: 'precis',
      parcours: 'alcool',
      preferences: etat.preferences.slice(),
      couleurs: etat.couleurs.slice(),
      categories: etat.categories.slice(),
      exclusions: listeDe(CFG.exclusions),
      avant: listeDe(CFG.mise_en_avant),
      budgetTotal: 0,
      budgetMaxBouteille: etat.budgetMax || 0,
      bio: etat.preferences.indexOf('bio') > -1,
      local: etat.preferences.indexOf('local') > -1,
      saison: Moteur.saisonCourante(),
      moment: Moteur.momentDetecte([etat.menu]),
      produitsRecettes: {}
    };
  }

  /* =====================================================================
     CALCUL
     ---------------------------------------------------------------------
     Une seule passe : découper la phrase, lire chaque plat, sortir un
     éventail de bouteilles par moment du repas, et calculer les
     quantités. Pas de « formules » à comparer : c'est le client qui
     compose la sienne en cliquant.
     ===================================================================== */

  /* LE SOMMELIER — appelé sur CHAQUE plat.
     Il ne choisit aucune bouteille et ne voit pas le catalogue : il
     décrit LE VIN QU'IL FAUT — couleur, tanin, fraîcheur, corps,
     température de service — comme un chef sommelier le dirait à son
     commis. Le moteur connaît déjà cette structure pour les 401
     bouteilles de la cave : il cherche la plus proche, parmi celles qui
     sont réellement en stock, au vrai prix, dans le couloir du badge, et
     sans jamais lever les interdits de la bibliothèque.

     Deux usages selon ce que la lecture locale a donné :
       — plat RECONNU : on garde la lecture locale (familles, portes
         fermées, terroir, recettes maison) et on ajoute seulement la
         précision du sommelier ;
       — plat INCONNU : la lecture de l'IA remplace la lecture prudente.

     Chaque plat n'est analysé qu'UNE FOIS dans la vie du site : la
     réponse est gardée définitivement. Service absent, lent ou en
     panne : rien ne change, l'assistant garde ce qu'il sait faire seul. */
  var platsTraduits = {};
  function consulterLeSommelier(lignesMenu) {
    var url = CFG.traducteur;
    if (!url) return;
    lignesMenu.forEach(function (l) {
      if (!l.analyse) return;
      var cle = String(l.texte || '').toLowerCase().trim();
      if (!cle || platsTraduits[cle] !== undefined) return;
      platsTraduits[cle] = null;              /* en cours : on n'y revient pas */
      var inconnu = !!l.analyse.inconnu;
      Moteur.comprendrePlat(l.texte, l.etape, url).then(function (lecture) {
        if (!lecture) return;
        var relu = inconnu ? Moteur.relireAvecLecture(lecture, l.etape, CFG.regles) : null;
        if (!inconnu) {
          /* Plat reconnu : on ne touche pas à la lecture, on l'enrichit. */
          relu = l.analyse;
          Moteur.poserVinIdeal(relu, lecture);
        }
        if (!relu) return;
        platsTraduits[cle] = relu;
        calculer();
        rendreResultats();
      });
    });
  }

  function calculer() {
    var lignesMenu = Moteur.decouperMenu(etat.menu, CFG.regles);
    if (!lignesMenu.length) { resultat = null; return; }

    /* Une lecture déjà obtenue du traducteur remplace la lecture
       prudente — et seulement celle-là. */
    lignesMenu.forEach(function (l) {
      var relu = platsTraduits[String(l.texte || '').toLowerCase().trim()];
      if (relu) l.analyse = relu;
    });
    consulterLeSommelier(lignesMenu);

    var etapes = lignesMenu.map(function (l) { return l.etape; });
    var ctx = contexte(etapes);

    /* Les recettes de la maison : leurs produits deviennent le signal le
       plus fort du moteur, et la recette elle-même est proposée en
       lecture. */
    var recettesParLigne = {};
    if (donnees.recettes && donnees.recettes.length) {
      lignesMenu.forEach(function (l, i) {
        var trouvees = Moteur.trouverRecettes(l.analyse, donnees.recettes, { cocktail: false });
        recettesParLigne[i] = trouvees;
        trouvees.slice(0, 2).forEach(function (t) {
          (t.recette.produits_liste || []).forEach(function (h) {
            ctx.produitsRecettes[h] = Math.max(ctx.produitsRecettes[h] || 0, t.exacte ? 1 : 0.45);
          });
        });
      });
    }

    var verres = Moteur.verresParEtape(ctx);
    var buveurs = Math.max(0, etat.convives - etat.sansAlcool);
    var combien = opt('propositions', 4);

    var lignes = lignesMenu.map(function (l, i) {
      /* La couleur imposée à CE plat prime sur celle du repas entier :
         un menu « plutôt rouge » n'oblige pas à boire du rouge sur les
         huîtres. Vide, la ligne suit le repas ; le repas vide, c'est le
         caviste qui décide. */
      var cle = String(i);
      var couleurLigne = etat.couleurLigne[cle];
      var ctxLigne = couleurLigne
        ? Object.assign({}, ctx, { couleurs: [couleurLigne] })
        : ctx;

      var candidats = Moteur.candidats(l.analyse, donnees.catalogue, ctxLigne);

      /* LE FILTRE NE DOIT JAMAIS VIDER LA RANGÉE EN SILENCE.
         Si la couleur demandée ne donne pas de quoi composer, on le dit
         et on retombe sur le conseil du caviste — plutôt qu'un écran
         vide dont le client ne comprendrait pas la cause. */
      var replii = null;
      var filtreCouleur = ctxLigne.couleurs && ctxLigne.couleurs.length ? ctxLigne.couleurs : null;
      var filtre = filtreCouleur || (ctxLigne.categories && ctxLigne.categories.length ? ctxLigne.categories : null);
      if (filtre && !candidats.length) {
        replii = filtreCouleur ? nomCouleur(filtreCouleur) : nomCategorie(ctxLigne.categories);
        candidats = Moteur.candidats(l.analyse, donnees.catalogue,
          Object.assign({}, ctxLigne, { couleurs: [], categories: [] }));
      }

      var choix = Moteur.eventail(candidats, combien, l.analyse.registre, l.analyse.texte || l.texte);

      /* Le plat admet plusieurs couleurs : la rangée doit les montrer.
         Sans filtre demandé par le client, bien sûr — s'il a réclamé du
         rouge, on ne va pas lui glisser un rosé. */
      if (!filtre) {
        choix = Moteur.diversifierCouleurs(choix, candidats, l.analyse.couleurs_admises);
      }

      /* Rangée plus courte que demandé : la cave n'avait pas quatre
         bouteilles de cette couleur. On l'annonce plutôt que de laisser
         croire à un éventail complet. */
      var courte = filtre && !replii && choix.length < combien ? choix.length : 0;

      /* RANGÉE COURTE FAUTE D'ACCORD ASSEZ BON — et non faute de couleur.
         Depuis que le moteur refuse de remplir une case avec un accord
         tiède (plancher de qualité), une rangée peut s'arrêter à deux ou
         trois bouteilles sans qu'aucun filtre soit actif. Le taire
         donnerait l'impression d'une panne ; le dire est un argument de
         caviste : on ne sert pas n'importe quoi pour faire nombre. */
      var courteQualite = (!courte && choix.length && choix.length < combien) ? choix.length : 0;

      /* La bouteille conseillée est le meilleur accord de l'éventail —
         pas la plus chère, pas la moins chère. */
      /* LE COULOIR DU BADGE « CONSEILLÉ » — 10 € à 30 € par défaut.
         Règle commerciale posée par Grégoire, et elle est double : le
         badge ne doit jamais tomber sur le premier prix (qui tire chaque
         panier vers le bas), ni sur le flacon de vitrine (qui ne se vend
         pas et fait fuir). Entre les deux, la fourchette où un client
         achète sans hésiter.

         Elle est sans risque parce qu'elle s'applique APRÈS le plancher
         de qualité : les bouteilles de la rangée ont déjà toutes franchi
         le niveau « bon accord ». Choisir parmi elles ne peut donc pas
         dégrader le conseil — les autres restent affichées et achetables,
         elles cessent seulement d'être mises en avant.

         Quand aucune bouteille n'est dans le couloir — une rangée de
         spiritueux qui démarre à 52 €, une rangée de petits vins qui
         plafonne à 9 € — on prend la plus PROCHE du couloir plutôt que
         de forcer : c'est ce qui évite le rhum à 295 € sur un café. */
      var conseille = null;
      var eligiblesBadge = choix;
      var clientVeutMoinsCher = (etat.preferences || []).indexOf('moins_cher') > -1;
      if (choix.length > 1 && !clientVeutMoinsCher) {
        var bMin = CFG.badge_min == null ? 10 : CFG.badge_min;
        var bMax = CFG.badge_max == null ? 30 : CFG.badge_max;
        var dansCouloir = choix.filter(function (c) {
          return c.produit.prix_eur >= bMin && c.produit.prix_eur <= bMax;
        });
        if (dansCouloir.length) {
          eligiblesBadge = dansCouloir;
        } else {
          /* Rien dans la fourchette : on vise le bord le plus proche.
             Rangée entièrement au-dessus → la plus abordable ; rangée
             entièrement en dessous → la plus chère. */
          var toutAuDessus = choix.every(function (c) { return c.produit.prix_eur > bMax; });
          var trie = choix.slice().sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });
          eligiblesBadge = [toutAuDessus ? trie[0] : trie[trie.length - 1]];
        }
      }
      eligiblesBadge.forEach(function (c) { if (!conseille || c.score > conseille.score) conseille = c; });

      /* TOUT CE QUI VA AUSSI AVEC CE PLAT.
         La rangée ne montre que quatre bouteilles, une par tranche de
         prix : sur des huîtres, cela laisse dans l'ombre une dizaine de
         blancs aussi bien notés — le client croit alors que la cave ne
         les a pas. On garde donc les meilleurs suivants, à découvrir
         d'un clic. Le seuil suit le meilleur accord de la rangée : on ne
         propose jamais « autre chose » qui serait manifestement moins
         bon. */
      var montres = {};
      choix.forEach(function (c) { montres[c.produit.handle] = true; });
      var plancher = choix.length ? (conseille ? conseille.score : choix[0].score) * 0.7 : 0;
      var autres = candidats
        .filter(function (c) { return !montres[c.produit.handle] && c.score >= plancher; })
        .slice(0, 12)
        .sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });

      var enProposition = function (c) {
        var volume = buveurs * (verres[l.etape] || 0) * (c.produit.famille.verre || 12.5);
        return {
          produit: c.produit,
          score: c.score,
          conseille: c === conseille,
          quantite: Moteur.bouteilles(c.produit, volume, { marge: false }),
          explication: Moteur.expliquer(c.produit, [l.analyse])
        };
      };

      var propositions = choix.map(enProposition);
      var autresPropositions = autres.map(enProposition);

      return {
        cle: cle,
        etape: l.etape,
        titre: l.texte,
        analyse: l.analyse,
        propositions: propositions,
        autres: autresPropositions,
        recettes: recettesParLigne[i] || [],
        couleurDemandee: couleurLigne || '',
        replii: replii,
        courte: courte,
        courteQualite: courteQualite
      };
    });

    /* Parcours sans alcool : une ligne en plus, jamais un remplacement. */
    if (etat.sansAlcool > 0) {
      var ctxSoft = Object.assign({}, ctx, { sansAlcool: true, preferences: [], couleurs: [], categories: [] });
      var refSoft = lignesMenu.filter(function (l) { return l.etape === 'plat'; })[0] || lignesMenu[0];
      var candSoft = Moteur.candidats(refSoft.analyse, donnees.catalogue, ctxSoft);
      var choixSoft = Moteur.eventail(candSoft, combien);
      if (choixSoft.length) {
        var meilleurSoft = null;
        choixSoft.forEach(function (c) { if (!meilleurSoft || c.score > meilleurSoft.score) meilleurSoft = c; });
        lignes.push({
          cle: 'soft',
          etape: 'soft',
          titre: 'Pour les ' + etat.sansAlcool + ' convive' + (etat.sansAlcool > 1 ? 's' : '') + ' sans alcool',
          analyse: refSoft.analyse,
          propositions: choixSoft.map(function (c) {
            var vol = etat.sansAlcool * Savoir.cl_sans_alcool_par_personne;
            return {
              produit: c.produit, score: c.score, conseille: c === meilleurSoft,
              quantite: Moteur.bouteilles(c.produit, vol, { marge: false }),
              explication: Moteur.expliquer(c.produit, [refSoft.analyse])
            };
          }),
          autres: [],
          recettes: []
        });
      }
    }

    resultat = { lignes: lignes, ctx: ctx };

    /* Pré-sélection : la bouteille conseillée de chaque ligne. Le client
       arrive donc sur une sélection complète et cohérente, qu'il n'a
       plus qu'à retoucher — plutôt que sur une page de cases vides. */
    lignes.forEach(function (ligne) {
      if (etat.choix[ligne.cle] !== undefined) {
        /* Le choix précédent est conservé s'il est toujours proposé. */
        var encore = toutesPropositions(ligne).some(function (p) { return p.produit.handle === etat.choix[ligne.cle]; });
        if (encore || etat.choix[ligne.cle] === null) return;
      }
      var c = ligne.propositions.filter(function (p) { return p.conseille; })[0] || ligne.propositions[0];
      etat.choix[ligne.cle] = c ? c.produit.handle : null;
    });
  }

  /* TOUTES les bouteilles offertes pour cette ligne — les quatre de la
     rangée ET celles du dépliage. Depuis qu'on peut choisir dans la
     seconde liste, chercher la bouteille retenue dans la seule première
     revenait à ne pas la trouver : la ligne se croyait écartée, et son
     sélecteur de quantité comme son bouton d'ajout disparaissaient. */
  function toutesPropositions(ligne) {
    return (ligne.autres && ligne.autres.length)
      ? ligne.propositions.concat(ligne.autres)
      : ligne.propositions;
  }

  function propositionRetenue(ligne) {
    var h = etat.choix[ligne.cle];
    if (!h) return null;
    return toutesPropositions(ligne).filter(function (p) { return p.produit.handle === h; })[0] || null;
  }

  function quantiteDe(ligne, prop) {
    if (!prop) return 0;
    if (etat.qte[ligne.cle] != null) return etat.qte[ligne.cle];
    if (etat.magnum[ligne.cle] && prop.quantite.magnum) return prop.quantite.magnum.nombre;
    return prop.quantite.confortable;
  }
  function prixUnitaireDe(ligne, prop) {
    if (etat.magnum[ligne.cle] && prop.quantite.magnum) return prop.quantite.magnum.prix_unitaire;
    return prop.produit.prix_eur;
  }

  function totaux() {
    var t = { total: 0, bouteilles: 0, lignes: 0, articles: [] };
    if (!resultat) return t;
    resultat.lignes.forEach(function (ligne) {
      var p = propositionRetenue(ligne);
      if (!p) return;
      var n = quantiteDe(ligne, p);
      if (n <= 0) return;
      var mag = etat.magnum[ligne.cle] && p.quantite.magnum;
      t.total += n * prixUnitaireDe(ligne, p);
      t.bouteilles += n;
      t.lignes++;
      var id = mag ? p.quantite.magnum.variante : p.produit.variante;
      if (id && p.produit.dispo) t.articles.push({ id: parseInt(id, 10), quantity: n });
    });
    t.total = Math.round(t.total * 100) / 100;
    return t;
  }

  /* =====================================================================
     RENDU — la saisie
     ===================================================================== */

  var EXEMPLES = [
    'magret de canard aux cèpes',
    'huîtres, saumon fumé',
    'raclette',
    'barbecue, entrecôte et saucisses',
    'foie gras, chapon, bûche au chocolat'
  ];

  /* LES DEUX BIBLIOTHÈQUES.
     Qui ne sait pas quoi servir n'a rien à écrire dans le champ : il lui
     faut d'abord des idées. Les 337 recettes de cuisine et les 292
     cocktails sont exactement cela, et ils n'étaient accessibles depuis
     nulle part sur cette page — les adresses dormaient dans la
     configuration sans qu'aucun lien ne les serve. */
  function htmlBibliotheques(titre) {
    var lc = (CFG.liens && CFG.liens.recettes) || '/blogs/recettes-de-cuisine';
    var lk = (CFG.liens && CFG.liens.cocktails) || '/blogs/recettes-de-cocktails';
    var h = '<div class="cl-som__biblios">';
    if (titre) h += '<span class="cl-som__biblios-lbl">' + esc(titre) + '</span>';
    h += '<a class="cl-som__biblio" href="' + esc(lc) + '">' +
      '<span class="cl-som__biblio-nom">Nos recettes de cuisine</span>' +
      '<span class="cl-som__biblio-fleche" aria-hidden="true">→</span></a>';
    h += '<a class="cl-som__biblio" href="' + esc(lk) + '">' +
      '<span class="cl-som__biblio-nom">Nos recettes de cocktails</span>' +
      '<span class="cl-som__biblio-fleche" aria-hidden="true">→</span></a>';
    h += '</div>';
    return h;
  }

  /* Les deux listes de puces facultatives. Tant que la cave n'est pas
     lue, on montre tout ; une fois lue, on ne montre que ce qui a
     réellement une bouteille achetable derrière. */
  function htmlPucesCouleur() {
    var dispo = donnees.catalogue ? Moteur.couleursDisponibles(donnees.catalogue) : null;
    return Savoir.couleurs.filter(function (c) {
      return !dispo || dispo[c.id];
    }).map(function (c) {
      var on = etat.couleurs.indexOf(c.id) > -1;
      var n = dispo ? dispo[c.id] : 0;
      return '<button type="button" class="cl-som__puce' + (on ? ' is-actif' : '') +
        '" data-couleur="' + c.id + '" aria-pressed="' + on + '"' +
        (n ? ' title="' + n + ' bouteille' + (n > 1 ? 's' : '') + ' disponible' + (n > 1 ? 's' : '') + '"' : '') +
        '>' + esc(c.nom) + '</button>';
    }).join('');
  }
  function htmlPucesCategorie() {
    var dispo = donnees.catalogue ? Moteur.categoriesDisponibles(donnees.catalogue) : null;
    return Savoir.categories.filter(function (c) {
      return !dispo || dispo[c.id];
    }).map(function (c) {
      var on = etat.categories.indexOf(c.id) > -1;
      return '<button type="button" class="cl-som__puce' + (on ? ' is-actif' : '') +
        '" data-categorie="' + c.id + '" aria-pressed="' + on + '">' + esc(c.nom) + '</button>';
    }).join('');
  }
  function majPuces() {
    var a = racine.querySelector('[data-puces="couleur"]');
    var b = racine.querySelector('[data-puces="categorie"]');
    if (a) a.innerHTML = htmlPucesCouleur();
    if (b) b.innerHTML = htmlPucesCategorie();
  }

  function rendreSaisie() {
    var h = '';
    h += '<div class="cl-som__champs">';

    h += '<div class="cl-som__champ-menu">';
    h += '<label class="cl-som__label" for="cl-som-menu">' + esc(Savoir.textes.label_menu) + '</label>';
    h += '<input class="cl-som__input" id="cl-som-menu" type="text" autocomplete="off" ' +
      'list="cl-som-suggestions" data-menu value="' + esc(etat.menu) + '" ' +
      'placeholder="huîtres, magret aux cèpes, roquefort, fondant au chocolat">';
    h += '<p class="cl-som__aide">Écrivez tout d’un trait, séparé par des virgules. Nous reconnaissons chaque moment du repas.</p>';
    h += '</div>';

    /* Le compteur de convives reprend exactement le geste des fiches
       recettes (− valeur +, valeur annoncée aux lecteurs d'écran) : le
       client l'a déjà en main. Chaque clic recalcule les bouteilles à
       l'instant, sans rien recharger. */
    h += '<div class="cl-som__champ-convives">';
    h += '<span class="cl-som__label" id="cl-som-lbl-convives">Nombre de convives</span>';
    h += '<div class="cl-som__compteur" role="group" aria-labelledby="cl-som-lbl-convives">';
    h += '<button type="button" class="cl-som__rond" data-convives="-1" aria-label="Un convive de moins">−</button>';
    h += '<output class="cl-som__compteur-val" data-val-convives aria-live="polite">' + etat.convives + '</output>';
    h += '<button type="button" class="cl-som__rond" data-convives="1" aria-label="Un convive de plus">+</button>';
    h += '</div>';
    h += '</div>';

    h += '<button type="button" class="cl-som__btn cl-som__btn--cta" data-lancer>Voir mes bouteilles</button>';
    h += '</div>';

    /* Le calibrage des quantités, en clair et à portée de clic. Le
       réglage par défaut est celui du service en restaurant ; le client
       le corrige selon ses convives, et tout se recalcule aussitôt. */
    h += '<div class="cl-som__soif">';
    h += '<span class="cl-som__soif-lbl" id="cl-som-lbl-soif">' +
      esc(Savoir.textes.label_conso).replace(/ \?$/, '&nbsp;?') + '</span>';
    h += '<div class="cl-som__soif-choix" role="group" aria-labelledby="cl-som-lbl-soif">';
    /* On affiche le nom LONG (« Légère », « Modérée »…) : la question porte
       désormais sur « la consommation », féminine — « Léger » ne s'accordait
       plus. Les deux formes existent déjà dans le savoir. */
    Savoir.niveaux_conso.forEach(function (n) {
      var on = n.id === etat.niveau;
      h += '<button type="button" class="cl-som__soif-btn' + (on ? ' is-actif' : '') + '" ' +
        'data-niveau="' + n.id + '" aria-pressed="' + on + '">' +
        '<span class="cl-som__soif-nom">' + esc(n.nom || n.court) + '</span>' +
        '<span class="cl-som__soif-det">' + esc(n.verres + (n.verres > 4 ? '+' : '') + ' verres') + '</span></button>';
    });
    h += '</div>';
    h += '<p class="cl-som__aide">Base : les doses de service du bar et du restaurant — verre de vin 12,5 cl, flûte 10 cl, dose de spiritueux 4 cl.</p>';
    h += '</div>';

    h += '<div class="cl-som__exemples"><span class="cl-som__exemples-lbl">Par exemple&nbsp;:</span>';
    EXEMPLES.forEach(function (ex) {
      h += '<button type="button" class="cl-som__ex" data-exemple="' + esc(ex) + '">' + esc(ex) + '</button>';
    });
    h += '</div>';

    /* Juste sous les exemples : c'est là qu'on est quand on ne sait pas
       quoi écrire. */
    h += htmlBibliotheques('À court d’idées ?');

    /* PRÉFÉRENCES — replié, et facultatif pour de bon : rien ici n'est
       exigé pour obtenir un résultat. Ce n'est pas un questionnaire de
       plus en amont, c'est une main tendue à qui sait déjà ce qu'il
       veut boire. On n'affiche que des choix que la cave peut honorer
       aujourd'hui : promettre du rosé quand il n'en reste pas une
       bouteille achetable serait mentir. */
    h += '<details class="cl-som__affiner"' + (etat.couleurs.length || etat.categories.length ? ' open' : '') +
      '><summary>Préférences : couleur & catégorie (facultatif)</summary>' +
      '<div class="cl-som__affiner-corps">';

    h += '<div class="cl-som__mini-champ cl-som__mini-champ--large"><span>Couleur</span>' +
      '<div class="cl-som__puces" data-puces="couleur">' + htmlPucesCouleur() + '</div></div>';

    h += '<div class="cl-som__mini-champ cl-som__mini-champ--large"><span>Catégorie</span>' +
      '<div class="cl-som__puces" data-puces="categorie">' + htmlPucesCategorie() + '</div></div>';

    h += '<p class="cl-som__aide">Sans rien cocher, c’est le caviste qui choisit la couleur, plat par plat. Une couleur cochée s’applique à tout le repas — vous pourrez toujours la changer sur un plat en particulier, une fois les bouteilles affichées.</p>';
    h += '</div></details>';

    /* Tout le reste est replié : on ne barre jamais la route avec des
       réglages dont le client n'a pas besoin. */
    h += '<details class="cl-som__affiner"><summary>Affiner (facultatif)</summary><div class="cl-som__affiner-corps">';

    h += '<label class="cl-som__mini-champ"><span>Convives sans alcool</span>' +
      '<input class="cl-som__input cl-som__input--court" type="number" min="0" max="200" value="' + etat.sansAlcool + '" data-reglage="sansAlcool"></label>';

    h += '<label class="cl-som__mini-champ"><span>Type de repas</span><select class="cl-som__input" data-reglage="typeRepas">' +
      Savoir.types_repas.map(function (t) {
        return '<option value="' + t.id + '"' + (t.id === etat.typeRepas ? ' selected' : '') + '>' + esc(t.nom) + '</option>';
      }).join('') + '</select></label>';

    h += '<label class="cl-som__mini-champ"><span>Maximum par bouteille (€)</span>' +
      '<input class="cl-som__input cl-som__input--court" type="number" min="0" step="5" value="' + (etat.budgetMax || '') + '" placeholder="libre" data-reglage="budgetMax"></label>';

    /* Les couleurs et les rayons ont leur propre bloc, en filtre ferme :
       ne restent ici que les envies qui n'excluent rien. */
    h += '<div class="cl-som__mini-champ cl-som__mini-champ--large"><span>Vos envies</span><div class="cl-som__puces">' +
      [['bio', 'Bio'], ['local', 'D’ici']].map(function (p) {
        var on = etat.preferences.indexOf(p[0]) > -1;
        return '<button type="button" class="cl-som__puce' + (on ? ' is-actif' : '') +
          '" data-pref="' + p[0] + '" aria-pressed="' + on + '">' + esc(p[1]) + '</button>';
      }).join('') + '</div></div>';

    h += '</div></details>';
    h += '<datalist id="cl-som-suggestions"></datalist>';

    zoneSaisie.innerHTML = h;
  }

  /* L'autocomplétion s'appuie sur les recettes réellement publiées : ce
     que le client choisit dans la liste, la cave sait l'accorder. */
  function remplirSuggestions() {
    var dl = d.getElementById('cl-som-suggestions');
    if (!dl || !donnees.recettes) return;
    var vus = {}, h = '';
    donnees.recettes.forEach(function (r) {
      if (r.est_cocktail) return;
      var t = r.titre.split(':')[0].trim();
      if (vus[t] || t.length > 55) return;
      vus[t] = 1;
      h += '<option value="' + esc(t) + '"></option>';
    });
    dl.innerHTML = h;
  }

  /* =====================================================================
     RENDU — les résultats
     ===================================================================== */

  function rendreResultats() {
    if (donnees.erreur) { zoneResultat.innerHTML = htmlErreur(); majBarre(); return; }
    /* Un écran qui reste blanc laisse croire à une panne. S'il n'y a
       rien à proposer, on le dit et on montre la sortie. */
    if (!resultat || !resultat.lignes.length) {
      zoneResultat.innerHTML = '<div class="cl-som__vide"><p>Nous n’avons pas réussi à lire de plat dans cette phrase. ' +
        'Essayez quelques mots simples — « magret de canard », « huîtres », « raclette » — ou séparez vos plats par des virgules.</p>' +
        '<p><a class="cl-som__lien" href="' + esc((CFG.liens && CFG.liens.contact) || '/pages/contact') + '">Demander conseil à un caviste</a></p></div>';
      majBarre();
      return;
    }

    var h = '';

    /* UN FILTRE ACTIF DOIT SE VOIR.
       Le brouillon de session restaure les préférences d'une visite
       précédente, et le bloc qui les porte est replié : on pouvait donc
       arriver sur une page qui ne montrait que des rhums sans qu'aucun
       élément à l'écran n'en donne la raison. On l'annonce en tête des
       résultats, avec de quoi tout rouvrir d'un clic. */
    if (etat.couleurs.length || etat.categories.length) {
      var dits = [];
      if (etat.couleurs.length) dits.push(nomCouleur(etat.couleurs));
      if (etat.categories.length) dits.push(nomCategorie(etat.categories));
      h += '<p class="cl-som__filtre-actif">Vous ne voyez que&nbsp;: <strong>' +
        esc(dits.join(' · ')) + '</strong>' +
        '<button type="button" class="cl-som__lien" data-vider-filtres>Tout afficher</button></p>';
    }

    resultat.lignes.forEach(function (ligne) { h += htmlLigne(ligne); });

    h += htmlPourAller();
    if (DEBUG) h += htmlDiagnostic();
    zoneResultat.innerHTML = h;
    majBarre();
  }

  function htmlLigne(ligne) {
    var h = '<section class="cl-som__ligne" data-ligne="' + esc(ligne.cle) + '">';

    h += '<header class="cl-som__ligne-tete">';
    if (ligne.etape !== 'soft') {
      h += '<p class="cl-som__moment">' + esc(nomEtape(ligne.etape)) + '</p>';
    }
    h += '<h2 class="cl-som__ligne-titre">' + esc(ligne.titre) + '</h2>';

    /* Quand le plat a été reconnu malgré une faute de frappe, on le dit :
       le client voit tout de suite si l'on a bien compris, et peut
       corriger sa phrase plutôt que de se demander d'où sortent ces
       bouteilles. */
    if (ligne.analyse && ligne.analyse.corrige) {
      h += '<p class="cl-som__compris">Compris comme&nbsp;: <strong>' +
        esc(ligne.analyse.corrige) + '</strong></p>';
    }

    /* La recette de la maison, quand elle existe vraiment : un lien, pas
       un pavé. */
    h += '</header>';

    /* LA RECETTE DE LA MAISON — une carte, plus un lien.
       En 13 px souligné, personne ne la voyait : elle passait pour une
       mention légale coincée sous le titre. Les 337 recettes portent
       une photo, un temps et des portions ; les montrer coûte une
       vignette et transforme une ligne perdue en invitation. */
    var rec = ligne.recettes[0];
    if (opt('recettes', true) && rec) {
      var r = rec.recette;
      h += '<a class="cl-som__recette" href="' + esc(r.url) + '">';
      if (r.image) {
        h += '<span class="cl-som__recette-img">' +
          '<img src="' + esc(r.image) + '" alt="" loading="lazy" width="64" height="64">' +
          '</span>';
      }
      h += '<span class="cl-som__recette-txt">';
      h += '<span class="cl-som__recette-eti">' +
        esc(rec.exacte ? 'La recette de la maison' : 'Une recette proche') + '</span>';
      h += '<span class="cl-som__recette-titre">' + esc(r.titre) + '</span>';

      var meta = [];
      if (r.temps) meta.push(r.temps);
      if (r.portions) meta.push(r.portions);
      if (r.difficulte) meta.push(r.difficulte);
      if (meta.length) {
        h += '<span class="cl-som__recette-meta">' + esc(meta.join(' · ')) + '</span>';
      }
      h += '</span>';
      h += '<span class="cl-som__recette-fleche" aria-hidden="true">→</span>';
      h += '</a>';
    }

    /* LA COULEUR, PLAT PAR PLAT.
       Un menu complet ne se boit pas d'une seule couleur : le réglage
       du repas donne le ton, cette liste le contredit là où il faut.
       Une liste déroulante plutôt que six puces — sur un téléphone,
       elle tient sur une ligne et n'écarte pas les bouteilles. Elle
       n'apparaît pas sur la ligne sans alcool, qui n'a pas de couleur. */
    if (ligne.etape !== 'soft') {
      h += htmlCouleurLigne(ligne);
    }

    if (ligne.replii) {
      h += '<p class="cl-som__repli">Nous n’avons rien en ' + esc(ligne.replii) +
        ' qui tienne cet accord aujourd’hui. Voici ce que le caviste propose à la place.</p>';
    } else if (ligne.courte) {
      h += '<p class="cl-som__repli">La cave n’a que ' + ligne.courte + ' bouteille' +
        (ligne.courte > 1 ? 's' : '') + ' de cette couleur pour ce plat aujourd’hui.</p>';
    } else if (ligne.analyse && ligne.analyse.inconnu) {
      /* PLAT NON RECONNU — on le dit. Un conseil donné sur une devinette
         doit s'annoncer comme tel : le client peut alors préciser en deux
         mots (« bourguignon » → « bœuf bourguignon »), et il sait
         pourquoi la sélection est prudente. Le taire, c'est ce qui a
         permis à un chardonnay de se retrouver en tête d'un bourguignon. */
      h += '<p class="cl-som__repli">Nous n’avons pas reconnu « <strong>' +
        esc(ligne.texte || '') + '</strong> » avec certitude. Voici une sélection prudente — ' +
        'précisez le plat en quelques mots et nous affinerons.</p>';
    } else if (ligne.courteQualite) {
      h += '<p class="cl-som__repli">Nous ne vous proposons que ' + ligne.courteQualite +
        ' bouteille' + (ligne.courteQualite > 1 ? 's' : '') + ' sur ce plat : ' +
        'le reste de la cave ne donnerait pas un accord que nous défendrions. ' +
        'Mieux vaut deux bonnes bouteilles que quatre approximatives.</p>';
    }

    if (!ligne.propositions.length) {
      h += '<p class="cl-som__vide">' + esc(Savoir.textes.aucun_produit) +
        ' <a href="' + esc((CFG.liens && CFG.liens.contact) || '/pages/contact') + '">Demander conseil</a></p></section>';
      return h;
    }

    h += '<div class="cl-som__cartes" role="group" aria-label="Bouteilles proposées pour ' + esc(ligne.titre) + '">';
    ligne.propositions.forEach(function (p, i) {
      h += htmlCarte(ligne, p, etiquetteBudget(i, ligne.propositions.length));
    });
    h += '</div>';

    /* « Vous ne proposez même pas tel vin » : si, il est candidat — la
       rangée n'a que quatre places, une par tranche de prix. Le reste de
       ce que la cave sait accorder à ce plat tient ici, à un clic. */
    if (ligne.autres && ligne.autres.length) {
      var ouvert = !!etat.plus[ligne.cle];
      h += '<button type="button" class="cl-som__lien cl-som__plus" data-plus="' + esc(ligne.cle) + '" ' +
        'aria-expanded="' + ouvert + '">' +
        (ouvert ? 'Masquer les autres bouteilles'
                : 'Voir ' + ligne.autres.length + ' autre' + (ligne.autres.length > 1 ? 's' : '') +
                  ' bouteille' + (ligne.autres.length > 1 ? 's qui vont' : ' qui va') + ' avec ce plat') +
        '</button>';
      if (ouvert) {
        h += '<div class="cl-som__cartes cl-som__cartes--autres" role="group" ' +
          'aria-label="Autres bouteilles pour ' + esc(ligne.titre) + '">';
        ligne.autres.forEach(function (p) { h += htmlCarte(ligne, p, ''); });
        h += '</div>';
      }
    }

    h += '<div class="cl-som__detail" data-detail="' + esc(ligne.cle) + '">' + htmlDetail(ligne) + '</div>';
    h += '</section>';
    return h;
  }

  /* L'échelon de budget d'une carte, d'après sa place dans la rangée —
     qui est triée du moins cher au plus cher. Avec moins d'échelons que
     d'étiquettes, on étale sur toute la gamme plutôt que de prendre les
     premières : à deux propositions on lit « le prix juste » puis
     « l'exception », pas deux voisines. */
  function etiquetteBudget(i, total) {
    var libelles = Savoir.etiquettes_budget || [];
    /* Une bouteille seule n'est pas un éventail : rien à étager. */
    if (libelles.length < 2 || total < 2) return '';
    var rang = Math.round(i * (libelles.length - 1) / (total - 1));
    return libelles[Math.min(rang, libelles.length - 1)] || '';
  }

  /* Le choix de couleur d'une rangée. « Au choix » n'est pas une option
     par défaut faute de mieux : c'est le conseil du caviste, et il est
     nommé comme tel. */
  function htmlCouleurLigne(ligne) {
    var dispo = donnees.catalogue ? Moteur.couleursDisponibles(donnees.catalogue) : null;
    var actuelle = ligne.couleurDemandee || '';
    var lbl = 'cl-som-coul-lbl-' + ligne.cle;

    var h = '<div class="cl-som__couleurs">';
    h += '<span class="cl-som__couleurs-lbl" id="' + lbl + '">Couleur</span>';
    h += '<div class="cl-som__couleurs-choix" role="group" aria-labelledby="' + lbl + '">';

    /* « Au choix » est un choix comme un autre, et le premier : c'est la
       recommandation de la maison, pas une case vide. */
    h += '<button type="button" class="cl-som__pastille' + (actuelle ? '' : ' is-actif') +
      '" data-couleur-plat="' + esc(ligne.cle) + '|" aria-pressed="' + (actuelle ? 'false' : 'true') +
      '">Au choix</button>';

    Savoir.couleurs.forEach(function (c) {
      if (dispo && !dispo[c.id]) return;
      var on = actuelle === c.id;
      h += '<button type="button" class="cl-som__pastille' + (on ? ' is-actif' : '') +
        '" data-couleur-plat="' + esc(ligne.cle) + '|' + c.id + '" aria-pressed="' + on + '">' +
        '<span class="cl-som__pastille-pt cl-som__pastille-pt--' + c.id + '" aria-hidden="true"></span>' +
        esc(c.nom) + '</button>';
    });

    h += '</div></div>';
    return h;
  }

  function htmlCarte(ligne, p, etiquette) {
    var pr = p.produit;
    var choisi = etat.choix[ligne.cle] === pr.handle;
    var q = p.quantite.confortable;

    var h = '<button type="button" class="cl-som__carte' + (choisi ? ' is-choisi' : '') + '" ' +
      'data-choisir="' + esc(ligne.cle) + '|' + esc(pr.handle) + '" aria-pressed="' + choisi + '">';

    h += '<span class="cl-som__carte-haut">';
    if (p.conseille) h += '<span class="cl-som__badge">Conseillé</span>';
    h += '<span class="cl-som__coche" aria-hidden="true">✓</span>';
    h += '</span>';

    /* L'échelon de budget, au-dessus du flacon. Il est rendu même vide
       pour que les quatre cartes gardent la même hauteur de tête : une
       rangée décalée se lit mal. */
    h += '<span class="cl-som__etiquette">' + esc(etiquette || '') + '</span>';

    h += '<span class="cl-som__carte-img">';
    h += pr.image
      ? '<img src="' + esc(pr.image) + '" alt="" loading="lazy" width="90" height="120">'
      : '<span class="cl-som__img-vide"></span>';
    h += '</span>';

    h += '<span class="cl-som__carte-nom">' + esc(pr.titre) + '</span>';
    if (pr.appellation) h += '<span class="cl-som__carte-app">' + esc(pr.appellation) + '</span>';
    h += '<span class="cl-som__carte-prix">' + esc(prix(pr.prix_eur)) + '</span>';
    h += '<span class="cl-som__carte-qte">' + q + ' bouteille' + (q > 1 ? 's' : '') +
      ' · ' + esc(prix(q * pr.prix_eur)) + '</span>';
    h += '</button>';
    return h;
  }

  /* Sous la rangée : tout ce qui compte une fois le choix fait —
     l'accord, le service, la quantité. Rien tant que rien n'est choisi. */
  function htmlDetail(ligne) {
    var p = propositionRetenue(ligne);
    if (!p) {
      return '<p class="cl-som__ecarte">Ce moment du repas est écarté de votre sélection. ' +
        '<button type="button" class="cl-som__lien" data-reprendre="' + esc(ligne.cle) + '">Le remettre</button></p>';
    }
    var pr = p.produit;
    var n = quantiteDe(ligne, p);
    var mag = etat.magnum[ligne.cle] && p.quantite.magnum;

    var h = '';
    h += '<p class="cl-som__accord">' + esc(p.explication.court) + '</p>';
    if (p.explication.reserve) h += '<p class="cl-som__reserve">' + esc(p.explication.reserve) + '</p>';

    var faits = [];
    var temp = formaterTemperature(pr.temperature);
    if (temp) faits.push('Service ' + temp);
    var ouv = texteUtile(pr.aeration);
    if (ouv) faits.push('Ouverture : ' + ouv);
    faits.push(mag ? 'Magnum 150 cl' : Math.round(pr.contenance) + ' cl');
    if (pr.degre) faits.push(pr.degre + (String(pr.degre).indexOf('%') > -1 ? '' : ' % vol.'));
    h += '<p class="cl-som__faits">' + esc(faits.join(' · ')) + '</p>';

    h += '<div class="cl-som__reglage-ligne">';
    h += '<div class="cl-som__compteur cl-som__compteur--petit" role="group" aria-label="Quantité pour ' + esc(ligne.titre) + '">';
    h += '<button type="button" class="cl-som__rond" data-qte="' + esc(ligne.cle) + '|-1" aria-label="Une bouteille de moins">−</button>';
    h += '<output class="cl-som__compteur-val">' + n + '</output>';
    h += '<button type="button" class="cl-som__rond" data-qte="' + esc(ligne.cle) + '|1" aria-label="Une bouteille de plus">+</button>';
    h += '</div>';

    if (p.quantite.magnum) {
      h += '<label class="cl-som__magnum"><input type="checkbox" data-magnum="' + esc(ligne.cle) + '"' + (mag ? ' checked' : '') + '> ' +
        esc(p.quantite.magnum.nombre + ' magnum' + (p.quantite.magnum.nombre > 1 ? 's' : '') +
            ' au lieu de ' + p.quantite.magnum.remplace + ' bouteilles') +
        (p.quantite.magnum.economie > 1 ? esc(' (−' + prix(p.quantite.magnum.economie) + ')') : '') + '</label>';
    }

    /* AJOUT RAPIDE, PLAT PAR PLAT.
       Jusqu'ici, le seul chemin vers le panier était le bouton global de
       la barre du bas : tout ou rien. Or on veut souvent n'acheter que le
       vin du plat, en gardant le reste pour plus tard. Le bouton prend
       la bouteille retenue sur CETTE ligne, dans la quantité affichée
       juste à côté — magnum compris s'il est coché. */
    var variante = mag && p.quantite.magnum ? p.quantite.magnum.variante : pr.variante;
    if (pr.dispo && variante && n > 0) {
      h += '<button type="button" class="cl-som__btn cl-som__btn--fant cl-som__atc-ligne" ' +
        'data-ajout-ligne="' + esc(variante) + ':' + n + '">' +
        '<span class="cl-som__atc-txt">Ajouter ' + n + ' ' +
        (mag ? 'magnum' : 'bouteille') + (n > 1 ? 's' : '') + ' au panier</span></button>';
    }

    h += '<a class="cl-som__lien" href="' + esc(pr.url) + '">Voir la fiche</a>';
    h += '<button type="button" class="cl-som__lien" data-retirer="' + esc(ligne.cle) + '">Retirer</button>';
    h += '</div>';

    if (p.explication.long) {
      h += '<details class="cl-som__pourquoi"><summary>Pourquoi cette bouteille&nbsp;?</summary>' +
        '<p>' + esc(p.explication.long) + '</p></details>';
    }
    return h;
  }

  /* Bloc de fin, replié : cocktail, avis du caviste, partage. Il ne doit
     jamais concurrencer la sélection. */
  function htmlPourAller() {
    var h = '<div class="cl-som__fin">';

    if (opt('cocktails', true) && donnees.recettes && donnees.recettes.length) {
      var cock = cocktailPropose();
      /* LE COCKTAIL D'OUVERTURE — visible, plus replié.
         Derrière un « summary » gris, personne ne l'ouvrait : c'était
         pourtant la seule proposition d'apéritif de la page. Il prend
         donc la même carte que les recettes de plat — photo, titre,
         flèche — et ses ingrédients suivent en clair. Il reste dans le
         bloc de fin : il ne concurrence pas la sélection, il la
         prolonge. */
      if (cock) {
        var cr = cock.recette;
        h += '<section class="cl-som__cocktail">';
        h += '<p class="cl-som__cocktail-titre">Un cocktail pour ouvrir' +
          '<button type="button" class="cl-som__lien cl-som__cocktail-autre" data-autre-cocktail>' +
          'En proposer un autre</button></p>';

        h += '<a class="cl-som__recette" href="' + esc(cr.url) + '">';
        if (cr.image) {
          h += '<span class="cl-som__recette-img">' +
            '<img src="' + esc(cr.image) + '" alt="" loading="lazy" width="64" height="64"></span>';
        }
        h += '<span class="cl-som__recette-txt">';
        h += '<span class="cl-som__recette-eti">La recette du bar</span>';
        h += '<span class="cl-som__recette-titre">' + esc(cr.titre) + '</span>';
        h += '<span class="cl-som__recette-meta">pour ' + cock.verres + ' verres environ' +
          (cr.temps ? ' · ' + esc(cr.temps) : '') + '</span>';
        h += '</span>';
        h += '<span class="cl-som__recette-fleche" aria-hidden="true">→</span>';
        h += '</a>';

        var achetables = [];
        h += '<ul class="cl-som__ing">';
        cock.ingredients.forEach(function (x) {
          h += '<li>' + esc((x.quantite ? x.quantite + ' ' : '') + x.nom);
          if (x.produit) {
            h += ' — <a href="' + esc(x.produit.url) + '">' + esc(x.produit.titre) + '</a> ' + esc(prix(x.produit.prix_eur));
            if (x.produit.dispo && x.produit.variante) achetables.push(x.produit.variante + ':1');
          }
          h += '</li>';
        });
        h += '</ul>';
        if (achetables.length) {
          h += '<button type="button" class="cl-som__btn cl-som__btn--fant" data-ajout-multi="' + esc(achetables.join(',')) + '">' +
            '<span class="cl-som__atc-txt">Ajouter les ' + achetables.length + ' ingrédients disponibles</span></button>';
        }
        h += '</section>';
      }
    }

    h += htmlBibliotheques('Toutes nos recettes');

    h += '<details class="cl-som__replie"><summary>Faire vérifier par un caviste</summary><div class="cl-som__replie-corps">';
    h += '<p class="cl-som__aide">Nos suggestions sont un point de départ. Un caviste peut les confirmer, ou tout composer pour vous.</p>';
    h += '<form class="cl-som__form" method="post" action="/contact#cl-som-caviste" id="cl-som-caviste" accept-charset="UTF-8">';
    h += '<input type="hidden" name="form_type" value="contact"><input type="hidden" name="utf8" value="✓">';
    h += '<div class="cl-som__duo">';
    h += '<label class="cl-som__mini-champ"><span>Votre nom</span><input class="cl-som__input" type="text" name="contact[name]" required autocomplete="name"></label>';
    h += '<label class="cl-som__mini-champ"><span>Votre e-mail</span><input class="cl-som__input" type="email" name="contact[email]" required autocomplete="email"></label>';
    h += '</div>';
    h += '<label class="cl-som__mini-champ"><span>Votre message</span><textarea class="cl-som__input" name="contact[body]" rows="9" data-recap>' + esc(texteRecap()) + '</textarea></label>';
    h += '<button type="submit" class="cl-som__btn">Envoyer au caviste</button>';
    h += '</form></div></details>';

    h += '<p class="cl-som__outils">';
    h += '<button type="button" class="cl-som__lien" data-partager>Copier le lien de ma sélection</button>';
    h += '<button type="button" class="cl-som__lien" data-imprimer>Imprimer la liste</button>';
    h += '</p>';

    h += '</div>';
    return h;
  }

  /* Tableau de bord du mode diagnostic : tout ce qu'il faut pour
     comprendre une absence de proposition, et rien de personnel. */
  function htmlDiagnostic() {
    var cat = donnees.catalogue || [];
    var dispo = cat.filter(function (p) { return p.dispo; });
    var familles = {};
    dispo.forEach(function (p) { familles[p.famille.id] = (familles[p.famille.id] || 0) + 1; });

    var h = '<div class="cl-som__vide" style="margin-top:24px"><p><strong>Diagnostic</strong></p><ul>';
    h += '<li>Versions — interface ' + esc(VERSION) + ' · moteur ' + esc(Moteur.version || '?') +
      ' · savoir ' + esc(Savoir.version || '?') + (versionsOk ? ' (cohérentes)' : ' — <strong>INCOHÉRENTES : videz le cache</strong>') + '</li>';
    h += '<li>Catalogue : ' + cat.length + ' produits lus, ' + dispo.length + ' disponibles</li>';
    h += '<li>Recettes : ' + (donnees.recettes ? donnees.recettes.length : 'en cours de chargement') + '</li>';
    h += '<li>Collection lue : ' + esc(CFG.source || '—') + '</li>';
    h += '<li>Familles disponibles : ' + esc(Object.keys(familles).map(function (k) {
      return k + ' ' + familles[k];
    }).join(', ') || 'AUCUNE') + '</li>';
    (resultat ? resultat.lignes : []).forEach(function (l) {
      h += '<li>« ' + esc(l.titre) +' » → étape ' + esc(l.etape) +
        ', ' + l.propositions.length + ' proposition(s), familles visées : ' +
        esc(Object.keys(l.analyse.familles || {}).slice(0, 6).join(', ')) + '</li>';
    });
    h += '</ul></div>';
    return h;
  }

  /* LE VIVIER DE COCKTAILS.
     Quand l'apéritif du menu appelle vraiment des cocktails, on reste sur
     ceux-là. Sinon — et c'est le cas le plus fréquent — on ouvre à toute
     la bibliothèque : 292 recettes, autant s'en servir. Peu importe que
     tous les ingrédients ne soient pas en cave, c'est une idée de
     cocktail qu'on donne, pas une commande. */
  function poolCocktails() {
    var tous = donnees.recettes.filter(function (x) { return x.est_cocktail; });
    if (!tous.length) return [];
    var apero = resultat.lignes.filter(function (l) { return l.etape === 'apero'; })[0];
    var analyse = apero ? apero.analyse : Moteur.analyserPlat('aperitif', 'apero', CFG.regles);
    var pertinents = Moteur.trouverRecettes(analyse, donnees.recettes, { cocktail: true })
      .slice(0, 12).map(function (t) { return t.recette; });
    /* Sous trois correspondances, « pertinent » ne veut plus rien dire :
       on préfère la variété de toute la bibliothèque à deux recettes
       ressassées. */
    return pertinents.length >= 3 ? pertinents : tous;
  }

  /* Un tirage au sort, avec mémoire des derniers servis : sans elle, le
     hasard ramène régulièrement la même recette deux fois de suite et
     l'outil a l'air cassé. */
  function tirerCocktail(pool) {
    var frais = pool.filter(function (r) { return etat.cocktailsVus.indexOf(r.handle) < 0; });
    if (!frais.length) { etat.cocktailsVus = []; frais = pool; }
    var r = frais[Math.floor(Math.random() * frais.length)];
    etat.cocktailsVus.push(r.handle);
    if (etat.cocktailsVus.length > 30) etat.cocktailsVus.shift();
    return r;
  }

  function cocktailPropose() {
    var pool = poolCocktails();
    if (!pool.length) return null;

    /* Le cocktail retenu ne bouge pas quand on retouche une bouteille :
       il ne change qu'à une nouvelle demande, ou sur clic explicite. */
    var r = null;
    if (etat.cocktail) {
      r = pool.filter(function (x) { return x.handle === etat.cocktail; })[0] ||
          donnees.recettes.filter(function (x) { return x.handle === etat.cocktail; })[0];
    }
    if (!r) { r = tirerCocktail(pool); etat.cocktail = r.handle; }
    if (!r) return null;
    var dejaPrevu = {};
    resultat.lignes.forEach(function (l) {
      var p = propositionRetenue(l);
      if (p) dejaPrevu[p.produit.handle] = true;
    });
    var prep = Moteur.preparerCocktail(r, donnees.catalogue, { nbAdultes: etat.convives }, dejaPrevu);
    return prep.ingredients.length ? prep : null;
  }

  /* Chaque panne a sa cause et son message : accuser la connexion du
     visiteur quand c'est le thème qui est en cause ferait chercher au
     mauvais endroit. Et un « détail technique » repliable permet de nous
     envoyer l'information utile en une capture d'écran, au lieu de
     décrire une page blanche. */
  function htmlErreur() {
    var code = donnees.erreur ? String(donnees.erreur.message || '') : '';
    var texte;
    if (code === 'GABARIT_ABSENT') {
      texte = 'Le catalogue a répondu une page web au lieu de données. Le gabarit de lecture ' +
        '(« sommelier-json ») n’est pas présent sur le thème actuellement affiché : c’est le cas ' +
        'tant que les fichiers ne sont pas publiés sur le thème en ligne.';
    } else if (code === 'CATALOGUE_VIDE') {
      texte = 'La cave a répondu, mais sans aucun produit. La collection lue est peut-être vide ' +
        'ou mal nommée dans les réglages de la section.';
    } else if (code === 'DELAI') {
      texte = 'La cave met trop de temps à répondre. C’est souvent passager : réessayez dans un instant.';
    } else if (code.indexOf('HTTP 429') > -1) {
      texte = 'La boutique a reçu trop de demandes coup sur coup et nous a fait patienter. Réessayez dans quelques secondes.';
    } else {
      texte = (CFG.textes && CFG.textes.erreur_chargement) || 'Le catalogue n’a pas pu être chargé.';
    }

    var h = '<div class="cl-som__vide cl-som__vide--erreur"><p>' + esc(texte) + '</p>';
    h += '<p><button type="button" class="cl-som__btn" data-lancer>Réessayer</button> ' +
      '<a class="cl-som__lien" href="' + esc((CFG.liens && CFG.liens.contact) || '/pages/contact') +
      '">Demander conseil à un caviste</a></p>';
    h += '<details class="cl-som__pourquoi"><summary>Détail technique</summary><p>' +
      esc(diagnostic()) + '</p></details>';
    h += '</div>';
    return h;
  }

  /* Ce qu'il faut savoir pour comprendre une panne, en une ligne, sans
     aucune donnée personnelle. */
  function diagnostic() {
    var bouts = [];
    bouts.push('erreur : ' + (donnees.erreur ? (donnees.erreur.message || 'inconnue') : 'aucune'));
    bouts.push('catalogue : ' + (donnees.catalogue ? donnees.catalogue.length + ' produits' : 'non chargé'));
    bouts.push('recettes : ' + (donnees.recettes ? donnees.recettes.length : 'non chargées'));
    bouts.push('collection : ' + (CFG.source || '—'));
    bouts.push('moments lus : ' + (resultat ? resultat.lignes.length : 0));
    try {
      var apercu = new URLSearchParams(w.location.search).get('preview_theme_id');
      bouts.push('aperçu de thème : ' + (apercu || 'non'));
    } catch (e) {}
    return bouts.join(' · ');
  }

  /* =====================================================================
     LA BARRE DU BAS — toujours visible, jamais à chercher
     ===================================================================== */

  /* La barre est fixée à la fenêtre : c'est donc au document de lui
     réserver sa place, sans quoi le dernier bloc et le haut du pied de
     page restent sous elle, inatteignables. On mesure sa hauteur réelle
     — elle change avec la largeur de l'écran et la taille de police du
     visiteur — plutôt que d'inscrire un nombre en dur. */
  function majPlaceBarre() {
    var actif = !barre.hidden;
    d.body.classList.toggle('cl-som-barre-active', actif);
    if (!actif) { d.body.style.removeProperty('--cl-som-barre-h'); return; }
    var haut = barre.offsetHeight;
    if (haut) d.body.style.setProperty('--cl-som-barre-h', haut + 'px');
  }

  function majBarre() {
    var t = totaux();
    if (!resultat || !t.lignes) { barre.hidden = true; majPlaceBarre(); return; }
    barre.hidden = false;
    barre.innerHTML =
      '<div class="cl-som__barre-corps">' +
        '<p class="cl-som__barre-chiffres">' +
          '<strong>' + esc(prix(t.total)) + '</strong>' +
          '<span>' + t.bouteilles + ' bouteille' + (t.bouteilles > 1 ? 's' : '') +
          ' · ' + esc(prix(etat.convives ? t.total / etat.convives : 0)) + ' par convive</span>' +
        '</p>' +
        '<button type="button" class="cl-som__btn cl-som__btn--cta cl-som__atc" data-ajout-tout>' +
          '<span class="cl-som__atc-txt">Ajouter au panier</span></button>' +
      '</div>';
    majPlaceBarre();
  }

  /* La hauteur de la barre change avec la largeur (le prix et le bouton
     passent sur deux lignes en dessous d'un certain point) : on la
     remesure plutôt que de figer la réserve du premier affichage. */
  if (w.ResizeObserver) {
    new w.ResizeObserver(function () { if (!barre.hidden) majPlaceBarre(); }).observe(barre);
  } else {
    w.addEventListener('resize', function () { if (!barre.hidden) majPlaceBarre(); });
  }

  /* =====================================================================
     MISES À JOUR CIBLÉES
     ---------------------------------------------------------------------
     On ne redessine jamais toute la page pour un clic : le visiteur
     perdrait sa position dans la liste. Seules la rangée concernée et la
     barre du bas sont rafraîchies.
     ===================================================================== */

  /* Redessine UNE rangée entière — utile quand sa structure change et
     pas seulement son état (dépliage des autres bouteilles). Le reste de
     la page ne bouge pas : le client garde sa position et son défilement. */
  function majLigneComplete(cle) {
    var ligne = resultat && resultat.lignes.filter(function (l) { return l.cle === cle; })[0];
    if (!ligne) return;
    var bloc = zoneResultat.querySelector('[data-ligne="' + cle + '"]');
    if (!bloc) return;
    bloc.outerHTML = htmlLigne(ligne);
    majBarre();
  }

  function majLigne(cle) {
    var ligne = resultat && resultat.lignes.filter(function (l) { return l.cle === cle; })[0];
    if (!ligne) return;
    var bloc = zoneResultat.querySelector('[data-ligne="' + cle + '"]');
    if (!bloc) return;
    Array.prototype.forEach.call(bloc.querySelectorAll('[data-choisir]'), function (btn) {
      var handle = btn.getAttribute('data-choisir').split('|')[1];
      var on = etat.choix[cle] === handle;
      btn.classList.toggle('is-choisi', on);
      btn.setAttribute('aria-pressed', String(on));
    });
    var detail = bloc.querySelector('[data-detail="' + cle + '"]');
    if (detail) detail.innerHTML = htmlDetail(ligne);
    majBarre();
    var zone = zoneResultat.querySelector('[data-recap]');
    if (zone) zone.value = texteRecap();
  }

  /* =====================================================================
     CHARGEMENT ET LANCEMENT
     ===================================================================== */

  /* ON N'ATTEND QUE LE CATALOGUE.
     Les recettes, c'est quatorze requêtes de plus : les attendre doublait
     le temps avant la première bouteille, pour un enrichissement dont le
     client n'a pas besoin pour choisir. Elles arrivent donc en arrière-
     plan et viennent compléter l'affichage quand elles sont prêtes —
     sans jamais bousculer une sélection déjà retouchée. */
  function charger() {
    if (donnees.charge) return Promise.resolve();
    return Moteur.chargerCatalogue(CFG.source, majAttente).then(function (cat) {
      donnees.catalogue = cat;
      donnees.charge = true;
      donnees.erreur = null;
      /* Les puces de couleur et de rayon ont été dessinées avant que la
         cave soit lue : on les corrige maintenant qu'on sait ce qui est
         réellement achetable. On ne redessine QUE ces deux listes — pas
         le formulaire — pour ne pas voler le curseur au client qui est
         peut-être en train d'écrire son menu. */
      majPuces();
      chargerRecettesEnFond();
    }).catch(function (e) { donnees.erreur = e; throw e; });
  }

  function chargerRecettesEnFond() {
    if (!opt('recettes', true) || donnees.recettes) return;
    Moteur.chargerRecettes(CFG.blogs).then(function (r) {
      donnees.recettes = r;
      remplirSuggestions();
      /* On ne redessine que si le client n'a encore rien retouché :
         personne ne doit voir son choix bouger tout seul. */
      if (resultat && !etat.aTouche) {
        try { calculer(); rendreResultats(); } catch (e) {}
      }
    }).catch(function () { donnees.recettes = []; });
  }

  function lancer() {
    var champ = zoneSaisie.querySelector('[data-menu]');
    if (champ) etat.menu = champ.value;
    if (!etat.menu.trim()) {
      annoncer('Écrivez d’abord votre menu ou votre plat.');
      if (champ) champ.focus();
      return;
    }
    if (!versionsOk) {
      zoneResultat.innerHTML = '<div class="cl-som__vide cl-som__vide--erreur">' +
        '<p>Votre navigateur a gardé une ancienne version de l’assistant en mémoire. ' +
        'Rechargez la page en forçant l’actualisation (Ctrl+Maj+R, ou Cmd+Maj+R sur Mac).</p></div>';
      return;
    }
    zoneResultat.innerHTML = htmlAttente();
    zoneResultat.setAttribute('aria-busy', 'true');
    donnees.erreur = null;
    etat.aTouche = false;
    /* Nouvelle demande, nouveau cocktail : c'est ce qui donne son
       intérêt à une bibliothèque de 292 recettes. */
    etat.cocktail = null;

    /* Un chargement qui n'aboutit jamais laissait l'écran sur son
       animation d'attente — que l'on prend pour une page blanche. Au
       bout de vingt secondes, on le dit. */
    var minuteur = setTimeout(function () {
      if (zoneResultat.getAttribute('aria-busy') !== 'true') return;
      donnees.erreur = new Error('DELAI');
      zoneResultat.setAttribute('aria-busy', 'false');
      rendreResultats();
    }, 20000);

    charger().then(function () {
      clearTimeout(minuteur);
      calculer();
      zoneResultat.setAttribute('aria-busy', 'false');
      rendreResultats();
      annoncer(resultat ? resultat.lignes.length + ' moments du repas, bouteilles proposées.' : 'Aucune proposition.');
      var premier = zoneResultat.querySelector('.cl-som__ligne-titre');
      if (premier) {
        premier.setAttribute('tabindex', '-1');
        premier.focus({ preventScroll: true });
        var y = zoneResultat.getBoundingClientRect().top + w.pageYOffset - 16;
        try { w.scrollTo({ top: y, behavior: 'smooth' }); } catch (e) { w.scrollTo(0, y); }
      }
      if (opt('mesure', true)) {
        Moteur.mesurer('recommandations', {
          moments: resultat ? resultat.lignes.length : 0,
          convives: etat.convives
        });
      }
      ecrireBrouillon();
    }).catch(function (err) {
      /* Ce `catch` couvre AUSSI les exceptions de `calculer()` et du
         rendu. Il ne doit donc jamais se contenter de redessiner une
         zone vide : une erreur silencieuse ressemble à une page blanche,
         et personne ne sait quoi en faire — ni le client, ni nous. */
      clearTimeout(minuteur);
      if (!donnees.erreur) donnees.erreur = err || new Error('INCONNUE');
      if (w.console && w.console.error) w.console.error('[Sommelier] échec :', err);
      zoneResultat.setAttribute('aria-busy', 'false');
      try { rendreResultats(); }
      catch (e2) {
        /* Dernier filet : même si le rendu lui-même casse, le visiteur
           voit un message et une porte de sortie. */
        zoneResultat.innerHTML = '<div class="cl-som__vide cl-som__vide--erreur">' +
          '<p>Une erreur inattendue nous a empêchés d’afficher la sélection.</p>' +
          '<p><button type="button" class="cl-som__btn" data-lancer>Réessayer</button> ' +
          '<a class="cl-som__lien" href="' + esc((CFG.liens && CFG.liens.contact) || '/pages/contact') +
          '">Demander conseil à un caviste</a></p></div>';
      }
    });
  }

  /* L'attente doit se voir et s'expliquer : lire tout le catalogue prend
     quelques secondes, et un écran silencieux pendant ce temps-là passe
     pour une panne — c'est exactement ce qui s'était produit. */
  function htmlAttente() {
    return '<div class="cl-som__attente" role="status">' +
      '<p class="cl-som__attente-titre"><span class="cl-som__spin" aria-hidden="true"></span>' +
      'Nous ouvrons la cave…</p>' +
      '<p class="cl-som__attente-det" data-attente>Lecture du catalogue : seules les bouteilles réellement ' +
      'disponibles vous seront proposées.</p>' +
      '<div class="cl-som__skeleton" aria-hidden="true"><span></span><span></span></div></div>';
  }

  function majAttente(page, pages, lus) {
    var el = zoneResultat.querySelector('[data-attente]');
    if (!el) return;
    el.textContent = 'Lecture du catalogue — ' + lus + ' bouteilles lues (' + page + '/' + pages + ')…';
  }

  /* =====================================================================
     RECALCUL INSTANTANÉ DES QUANTITÉS
     ---------------------------------------------------------------------
     Le nombre de convives et la façon de boire ne changent PAS les
     bouteilles proposées — seulement combien il en faut. On ne relance
     donc ni la lecture du catalogue ni la notation des accords : on
     recalcule les volumes et on réécrit les seuls chiffres à l'écran.
     Le client voit le nombre de bouteilles bouger sous son doigt, sans
     clignotement et sans perdre sa place dans la page.
     ===================================================================== */

  function majQuantites() {
    if (!resultat) return;
    var etapes = resultat.lignes
      .filter(function (l) { return l.etape !== 'soft'; })
      .map(function (l) { return l.etape; });
    var ctx = contexte(etapes);
    var verres = Moteur.verresParEtape(ctx);
    var buveurs = Math.max(0, etat.convives - etat.sansAlcool);

    /* Une quantité corrigée à la main ne veut plus rien dire dès que le
       nombre de convives change : on repart du calcul. */
    etat.qte = {};

    resultat.lignes.forEach(function (ligne) {
      toutesPropositions(ligne).forEach(function (p) {
        var volume = ligne.etape === 'soft'
          ? etat.sansAlcool * Savoir.cl_sans_alcool_par_personne
          : buveurs * (verres[ligne.etape] || 0) * (p.produit.famille.verre || 12.5);
        p.quantite = Moteur.bouteilles(p.produit, volume, { marge: false });
      });
    });

    /* Réécriture ciblée : le compte sur chaque carte, puis le bloc de
       détail de la bouteille retenue. */
    resultat.lignes.forEach(function (ligne) {
      var bloc = zoneResultat.querySelector('[data-ligne="' + ligne.cle + '"]');
      if (!bloc) return;
      toutesPropositions(ligne).forEach(function (p) {
        var carte = bloc.querySelector('[data-choisir="' + ligne.cle + '|' + p.produit.handle + '"]');
        if (!carte) return;
        var lbl = carte.querySelector('.cl-som__carte-qte');
        if (!lbl) return;
        var n = p.quantite.confortable;
        lbl.textContent = n + ' bouteille' + (n > 1 ? 's' : '') + ' · ' + prix(n * p.produit.prix_eur);
      });
      var detail = bloc.querySelector('[data-detail="' + ligne.cle + '"]');
      if (detail) detail.innerHTML = htmlDetail(ligne);
    });

    majBarre();
    var zone = zoneResultat.querySelector('[data-recap]');
    if (zone) zone.value = texteRecap();
    ecrireBrouillon();
  }

  /* Relance complète : réservée aux réglages qui changent les bouteilles
     elles-mêmes (envies, budget plafond, convives sans alcool). */
  function recalculer() {
    if (!resultat && !donnees.charge) return;
    if (!etat.menu.trim()) return;
    calculer();
    rendreResultats();
    ecrireBrouillon();
  }

  /* =====================================================================
     PANIER — réutilise le panier Eurus existant
     ===================================================================== */

  function sectionIdDe(sel) {
    var el = d.querySelector(sel);
    if (!el) return null;
    var host = el.closest('[id^="shopify-section-"]');
    return host ? host.id.replace('shopify-section-', '') : null;
  }
  function remplacerSection(id, html) {
    if (!id || !html) return;
    var host = d.getElementById('shopify-section-' + id);
    if (!host) return;
    var tmp = d.createElement('div');
    tmp.innerHTML = html;
    var frais = tmp.querySelector('#shopify-section-' + id);
    host.innerHTML = frais ? frais.innerHTML : tmp.innerHTML;
  }

  function ajouter(articles, bouton) {
    if (!articles.length) return Promise.resolve();
    var bulle = sectionIdDe('#cart-icon-bubble') || sectionIdDe('#mobile-cart-icon-bubble');
    var tiroir = sectionIdDe('#CartDrawer');
    var sections = [bulle, tiroir].filter(Boolean);
    var libelle = bouton ? bouton.querySelector('.cl-som__atc-txt') : null;
    var initial = libelle ? libelle.textContent : '';
    if (bouton) { bouton.setAttribute('aria-busy', 'true'); }
    if (libelle) libelle.textContent = 'Ajout en cours…';

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: articles, sections: sections, sections_url: w.location.pathname })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (bouton) bouton.setAttribute('aria-busy', 'false');
        if (!res.ok) throw new Error((res.data && res.data.description) || 'erreur');
        if (res.data.sections) {
          remplacerSection(bulle, res.data.sections[bulle]);
          remplacerSection(tiroir, res.data.sections[tiroir]);
        }
        if (libelle) libelle.textContent = 'Ajouté ✓';
        annoncer(articles.length > 1 ? articles.length + ' références ajoutées au panier.' : 'Bouteille ajoutée au panier.');
        try {
          d.dispatchEvent(new CustomEvent('eurus:cart:items-changed', { bubbles: true, detail: res.data }));
          d.dispatchEvent(new CustomEvent('eurus:product:added', { bubbles: true, detail: res.data }));
        } catch (e) {}
        if (opt('ouvrir_panier', true)) ouvrirTiroir();
        if (libelle) setTimeout(function () { libelle.textContent = initial; }, 2400);
      })
      .catch(function (err) {
        if (bouton) bouton.setAttribute('aria-busy', 'false');
        if (libelle) libelle.textContent = 'Réessayer';
        annoncer('L’ajout au panier a échoué : ' + (err && err.message ? err.message : 'stock insuffisant.'));
        if (libelle) setTimeout(function () { libelle.textContent = initial; }, 3000);
      });
  }

  /* Eurus pilote son tiroir par un store Alpine : c'est la voie du thème.
     On ne simule PAS un clic sur l'icône du panier — selon les réglages
     c'est un lien vers /cart, et le visiteur perdrait sa composition. */
  function ouvrirTiroir() {
    if (!d.querySelector('#CartDrawer')) return;
    try {
      if (w.Alpine && typeof w.Alpine.store === 'function') {
        var mini = w.Alpine.store('xMiniCart');
        if (mini && typeof mini.openCart === 'function') mini.openCart();
      }
    } catch (e) { /* le tiroir reste fermé : sans conséquence */ }
  }

  var vocal = null;
  function annoncer(msg) {
    if (!vocal) {
      vocal = d.createElement('div');
      vocal.className = 'cl-som__sr';
      vocal.setAttribute('aria-live', 'polite');
      vocal.setAttribute('role', 'status');
      d.body.appendChild(vocal);
    }
    vocal.textContent = '';
    setTimeout(function () { vocal.textContent = msg; }, 30);
  }

  /* =====================================================================
     RÉCAPITULATIF, PARTAGE, BROUILLON
     ===================================================================== */

  function texteRecap() {
    var L = [];
    L.push('Bonjour, voici la sélection préparée avec l’assistant en ligne.');
    L.push('');
    L.push('MENU : ' + etat.menu);
    L.push('CONVIVES : ' + etat.convives + (etat.sansAlcool ? ' (dont ' + etat.sansAlcool + ' sans alcool)' : ''));
    L.push('');
    if (resultat) {
      resultat.lignes.forEach(function (ligne) {
        var p = propositionRetenue(ligne);
        if (!p) return;
        var n = quantiteDe(ligne, p);
        L.push('- ' + (ligne.etape === 'soft' ? 'Sans alcool' : nomEtape(ligne.etape)) + ' (' + ligne.titre + ') : ' +
          n + ' × ' + p.produit.titre);
      });
      var t = totaux();
      L.push('');
      L.push('Total indicatif : ' + prix(t.total) + ' — ' + t.bouteilles + ' bouteilles');
    }
    L.push('');
    L.push('Pourriez-vous confirmer, ou proposer mieux ?');
    return L.join('\n');
  }

  function etatPartage() {
    return { m: etat.menu, c: etat.convives, s: etat.sansAlcool, n: etat.niveau,
             t: etat.typeRepas, b: etat.budgetMax, p: etat.preferences, x: etat.choix,
             k: etat.couleurs, g: etat.categories, kl: etat.couleurLigne };
  }
  function lienPartage() {
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(etatPartage()))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return w.location.origin + w.location.pathname + '#menu=' + b64;
  }
  function lireLien() {
    var m = /#menu=([A-Za-z0-9\-_]+)/.exec(w.location.hash);
    if (!m) return false;
    try {
      var o = JSON.parse(decodeURIComponent(escape(atob(m[1].replace(/-/g, '+').replace(/_/g, '/')))));
      appliquerEtat(o);
      return !!etat.menu;
    } catch (e) { return false; }
  }
  /* Entrée depuis une fiche recette : `?menu=moussaka+grecque&convives=6`.
     Lisible et partageable, contrairement au `#menu=` encodé qui sert au
     partage d'un panachage complet. Le résultat s'affiche sans que le visiteur
     ait rien à saisir — c'est toute la règle : la récompense avant l'effort. */
  function lireParam() {
    try {
      var q = new URLSearchParams(w.location.search);
      var m = q.get('menu');
      if (!m) return false;
      etat.menu = m.slice(0, 400);
      var c = parseInt(q.get('convives'), 10);
      if (c > 0 && c <= 300) etat.convives = c;
      return true;
    } catch (e) { return false; }
  }
  function appliquerEtat(o) {
    if (!o) return;
    if (o.m) etat.menu = o.m;
    if (o.c) etat.convives = o.c;
    if (o.s != null) etat.sansAlcool = o.s;
    if (o.n) etat.niveau = o.n;
    if (o.t) etat.typeRepas = o.t;
    if (o.b) etat.budgetMax = o.b;
    if (o.p) etat.preferences = o.p;
    if (o.k) etat.couleurs = o.k;
    if (o.g) etat.categories = o.g;
    if (o.kl) etat.couleurLigne = o.kl;
    if (o.x) etat.choix = o.x;
  }
  /* Brouillon de session : il disparaît à la fermeture de l'onglet, donc
     aucun consentement à demander. */
  function ecrireBrouillon() {
    try { w.sessionStorage.setItem('cl-sommelier', JSON.stringify(etatPartage())); } catch (e) {}
  }
  function lireBrouillon() {
    try {
      var o = JSON.parse(w.sessionStorage.getItem('cl-sommelier') || 'null');
      appliquerEtat(o);
      return !!(o && o.m);
    } catch (e) { return false; }
  }

  /* =====================================================================
     ÉVÉNEMENTS
     ===================================================================== */

  racine.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('button') : null;
    if (!b || !racine.contains(b)) return;
    var a;

    if (b.hasAttribute('data-lancer')) { lancer(); return; }

    if ((a = b.getAttribute('data-exemple'))) {
      var champ = zoneSaisie.querySelector('[data-menu]');
      etat.menu = champ.value = a;
      lancer();
      return;
    }

    if ((a = b.getAttribute('data-convives'))) {
      etat.convives = Math.max(1, Math.min(300, etat.convives + parseInt(a, 10)));
      var out = zoneSaisie.querySelector('[data-val-convives]');
      if (out) out.textContent = etat.convives;
      if (etat.sansAlcool > etat.convives) etat.sansAlcool = etat.convives;
      majQuantites();
      return;
    }

    if ((a = b.getAttribute('data-niveau'))) {
      etat.niveau = a;
      Array.prototype.forEach.call(zoneSaisie.querySelectorAll('[data-niveau]'), function (x) {
        var on = x.getAttribute('data-niveau') === a;
        x.classList.toggle('is-actif', on);
        x.setAttribute('aria-pressed', String(on));
      });
      majQuantites();
      if (opt('mesure', true)) Moteur.mesurer('niveau', { niveau: a });
      return;
    }

    if ((a = b.getAttribute('data-pref'))) {
      var i = etat.preferences.indexOf(a);
      if (i > -1) etat.preferences.splice(i, 1); else etat.preferences.push(a);
      b.classList.toggle('is-actif');
      b.setAttribute('aria-pressed', String(etat.preferences.indexOf(a) > -1));
      recalculer();
      return;
    }

    /* Couleur et rayon demandés pour tout le repas. Un second clic
       décoche : le filtre ferme doit pouvoir se relâcher aussi vite
       qu'il s'est posé. */
    if ((a = b.getAttribute('data-couleur'))) {
      basculer(etat.couleurs, a);
      b.classList.toggle('is-actif');
      b.setAttribute('aria-pressed', String(etat.couleurs.indexOf(a) > -1));
      recalculer();
      if (opt('mesure', true)) Moteur.mesurer('couleur', { couleurs: etat.couleurs.join(',') });
      return;
    }

    if ((a = b.getAttribute('data-categorie'))) {
      basculer(etat.categories, a);
      b.classList.toggle('is-actif');
      b.setAttribute('aria-pressed', String(etat.categories.indexOf(a) > -1));
      recalculer();
      if (opt('mesure', true)) Moteur.mesurer('categorie', { categories: etat.categories.join(',') });
      return;
    }

    /* Couleur imposée à un plat seul. On oublie la bouteille retenue sur
       cette ligne : la garder alors qu'elle vient d'être filtrée
       laisserait un rouge sélectionné dans une rangée de blancs. */
    /* Déplier les autres bouteilles d'une rangée. On ne redessine que
       cette rangée : le client garde sa place dans la page. */
    if ((a = b.getAttribute('data-plus'))) {
      etat.plus[a] = !etat.plus[a];
      majLigneComplete(a);
      if (opt('mesure', true)) Moteur.mesurer('voir_plus', { etape: a });
      return;
    }

    /* Tout rouvrir : les filtres du repas ET ceux posés plat par plat.
       Un seul geste doit suffire à revenir au conseil du caviste. */
    if (b.hasAttribute('data-vider-filtres')) {
      etat.couleurs = [];
      etat.categories = [];
      etat.couleurLigne = {};
      rendreSaisie();
      recalculer();
      if (opt('mesure', true)) Moteur.mesurer('filtres_vides', {});
      return;
    }

    /* Un autre cocktail : on ne recalcule pas les bouteilles, seul le
       bloc de fin change. La sélection du client reste intacte. */
    if (b.hasAttribute('data-autre-cocktail')) {
      etat.cocktail = null;
      var fin = racine.querySelector('.cl-som__fin');
      if (fin) fin.outerHTML = htmlPourAller();
      if (opt('mesure', true)) Moteur.mesurer('autre_cocktail', {});
      return;
    }

    if ((a = b.getAttribute('data-couleur-plat')) !== null) {
      var cp = a.split('|');
      if (cp[1]) etat.couleurLigne[cp[0]] = cp[1]; else delete etat.couleurLigne[cp[0]];
      delete etat.choix[cp[0]];
      delete etat.qte[cp[0]];
      delete etat.magnum[cp[0]];
      recalculer();
      if (opt('mesure', true)) Moteur.mesurer('couleur_plat', { couleur: cp[1] || 'auto' });
      return;
    }

    if ((a = b.getAttribute('data-choisir'))) {
      var pc = a.split('|');
      /* Un clic CHOISIT, toujours. Recliquer sur la bouteille déjà
         retenue ne la retire pas : ce serait un piège, on clique pour
         confirmer son choix. Pour écarter un moment du repas, il y a le
         bouton « Retirer », qui le dit. */
      if (etat.choix[pc[0]] !== pc[1]) {
        etat.aTouche = true;
        etat.choix[pc[0]] = pc[1];
        delete etat.qte[pc[0]];
        delete etat.magnum[pc[0]];
        majLigne(pc[0]);
        if (opt('mesure', true)) Moteur.mesurer('choix', { produit: pc[1] });
      }
      return;
    }

    if ((a = b.getAttribute('data-retirer'))) {
      etat.aTouche = true;
      etat.choix[a] = null; delete etat.qte[a]; delete etat.magnum[a];
      majLigne(a); return;
    }
    if ((a = b.getAttribute('data-reprendre'))) {
      var ligne = resultat.lignes.filter(function (l) { return l.cle === a; })[0];
      var c = ligne && (ligne.propositions.filter(function (p) { return p.conseille; })[0] || ligne.propositions[0]);
      etat.choix[a] = c ? c.produit.handle : null;
      majLigne(a); return;
    }

    if ((a = b.getAttribute('data-qte'))) {
      var pq = a.split('|'), cle = pq[0], delta = parseInt(pq[1], 10);
      var lg = resultat.lignes.filter(function (l) { return l.cle === cle; })[0];
      var pp = lg && propositionRetenue(lg);
      if (!pp) return;
      etat.aTouche = true;
      etat.qte[cle] = Math.max(0, quantiteDe(lg, pp) + delta);
      majLigne(cle); return;
    }

    /* Ajout d'une seule ligne du menu. Même mécanique que l'ajout global
       (tiroir de panier et pastille de compte rafraîchis), sur un seul
       article. */
    if ((a = b.getAttribute('data-ajout-ligne'))) {
      var un = a.split(':');
      ajouter([{ id: parseInt(un[0], 10), quantity: parseInt(un[1], 10) }], b);
      if (opt('mesure', true)) Moteur.mesurer('ajout_ligne', { bouteilles: parseInt(un[1], 10) });
      return;
    }

    if ((a = b.getAttribute('data-ajout-multi'))) {
      ajouter(a.split(',').map(function (x) {
        var y = x.split(':'); return { id: parseInt(y[0], 10), quantity: parseInt(y[1], 10) };
      }), b);
      if (opt('mesure', true)) Moteur.mesurer('ajout_cocktail', {});
      return;
    }

    if (b.hasAttribute('data-ajout-tout')) {
      var t = totaux();
      if (!t.articles.length) { annoncer('Aucune bouteille sélectionnée.'); return; }
      ajouter(t.articles, b);
      if (opt('mesure', true)) Moteur.mesurer('ajout_selection', { references: t.articles.length, bouteilles: t.bouteilles });
      return;
    }

    if (b.hasAttribute('data-imprimer')) { w.print(); return; }

    if (b.hasAttribute('data-partager')) {
      var lien = lienPartage();
      try { w.history.replaceState(null, '', lien); } catch (e) {}
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(lien)
          .then(function () { annoncer('Lien copié.'); b.textContent = 'Lien copié ✓'; })
          .catch(function () { annoncer('Le lien est dans la barre d’adresse.'); });
      } else annoncer('Le lien est dans la barre d’adresse.');
      if (opt('mesure', true)) Moteur.mesurer('partage', {});
      return;
    }
  });

  racine.addEventListener('change', function (e) {
    var t = e.target;
    var r = t.getAttribute && t.getAttribute('data-reglage');
    if (r) {
      etat[r] = (t.type === 'number') ? Math.max(0, parseFloat(t.value || 0)) : t.value;
      if (r === 'sansAlcool' && etat.sansAlcool > etat.convives) etat.sansAlcool = etat.convives;
      recalculer();
      return;
    }
    var mg = t.getAttribute && t.getAttribute('data-magnum');
    if (mg) { etat.magnum[mg] = t.checked; delete etat.qte[mg]; majLigne(mg); }
  });

  racine.addEventListener('input', function (e) {
    if (e.target.hasAttribute && e.target.hasAttribute('data-menu')) etat.menu = e.target.value;
  });

  /* Entrée dans le champ = lancer : c'est le geste attendu. */
  racine.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (e.target.hasAttribute && e.target.hasAttribute('data-menu')) { e.preventDefault(); lancer(); }
  });

  /* =====================================================================
     DÉMARRAGE
     ===================================================================== */

  var repris = lireLien() || lireParam() || lireBrouillon();
  rendreSaisie();
  majBarre();
  if (opt('mesure', true)) Moteur.mesurer('ouverture', {});
  if (repris && etat.menu) lancer();

})(window, document);
